import { ListButton } from "@refinedev/antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Flex,
  Modal,
  Spin,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  LeftOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "@/utilities";
import {
  API_URL_PIM_ATTRIBUTE_SETS,
  API_URL_PIM_DRAFTS,
  API_URL_PIM_PRODUCTS,
} from "@/services/XCONS_ENV";
import { HistorialDelProducto } from "./HistorialDelProducto";
import type {
  CatalogAttribute,
  CatalogDetail,
  CatalogGroup,
  DiffArea,
  DiffEntry,
  PendingDetail,
} from "./types";
import {
  Adjuntos,
  Bloque,
  Campo,
  CambiosDelBloque,
  CategoriasPorArbol,
  ImagenesYVideos,
  Relacionados,
  SeccionesDeLaFicha,
  Vacio,
  cantidadDeVinculos,
  seccionesDelForm,
  valorPublicado,
} from "./FichaSoloLectura";
import { skuFabricanteDe } from "./sku-xcons";
import { etiquetaConjunto } from "./conjuntos";
import { ProductStateSteps } from "./ProductStateSteps";
import { CabeceraFija } from "./CabeceraFija";
import { PermisosDelProducto } from "./PermisosDelProducto";
import { ModalDeRechazo } from "./ModalDeRechazo";
import type { AccionDeEstado } from "./ProductStateSteps";

const { Text, Title } = Typography;

/** Las áreas cuyos cambios son campos de la ficha, y se marcan en su celda. */
const AREAS_DE_CAMPOS: DiffArea[] = ["identidad", "valores"];

/**
 * Revisión de una versión pendiente (PIM Fase 14): **la ficha de la versión, con la
 * estructura del formulario** (xcons-docs#401), y las tres acciones de la compuerta.
 *
 * Sobre un producto publicado, lo que la versión cambia **se marca en la ficha**, con lo
 * publicado tachado debajo: se revisa el producto entero, sin perder de vista qué cambia.
 * En un alta no hay nada contra qué comparar, y la ficha es simplemente lo que se publica.
 *
 * **De dónde sale cada campo.** Los que la versión cambia, del diff, que ya trae la
 * etiqueta resuelta. El resto es lo publicado —el backend manda sólo lo que cambia— y en
 * un alta eso es vacío, `—`. Las secciones salen del producto publicado, que las trae sin
 * las opciones; sólo un alta, o un cambio de conjunto, pide el esquema.
 *
 * Dos cosas que se deciden acá y no en el backend:
 *
 * - **El motivo del rechazo es obligatorio.** Lo valida el backend, pero el form no deja
 *   mandarlo vacío: enterarse por un 400 de que hay que escribir algo es peor.
 * - **Los botones salen de `permissions`**, que el backend resuelve. Acá no se deduce
 *   "soy Admin y está en revisión": esa regla vive en un solo lugar.
 */
export const PendingReviewPage: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [pendiente, setPendiente] = useState<PendingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ejecutando, setEjecutando] = useState(false);
  const [rechazoAbierto, setRechazoAbierto] = useState(false);
  /** Lo publicado: la base de todo campo que la versión no cambia. `null` en un alta. */
  const [publicado, setPublicado] = useState<CatalogDetail | null>(null);
  const [grupos, setGrupos] = useState<CatalogGroup[]>([]);
  /** El conjunto de la versión, por nombre: el tipo de producto que se muestra. */
  const [nombreConjunto, setNombreConjunto] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get<PendingDetail>(
        `${API_URL_PIM_DRAFTS}/${encodeURIComponent(id)}`,
      );

      let base: CatalogDetail | null = null;
      if (!data.neverPublished) {
        try {
          ({ data: base } = await axiosInstance.get<CatalogDetail>(
            `${API_URL_PIM_PRODUCTS}/${encodeURIComponent(data.sku)}`,
          ));
        } catch {
          // Sin lo publicado la ficha igual muestra lo que la versión cambia.
        }
      }

      const conjunto = data.attributeSetId ?? data.payload?.attributeSetId;
      let secciones = base?.groups ?? [];
      let nombre = base?.attributeSet.name ?? null;
      if (conjunto && conjunto !== base?.attributeSet.id) {
        try {
          const { data: esquema } = await axiosInstance.get<{
            name?: string;
            groups: CatalogGroup[];
          }>(`${API_URL_PIM_ATTRIBUTE_SETS}/${conjunto}`);
          secciones = esquema.groups ?? [];
          nombre = esquema.name ?? nombre;
        } catch {
          // Sin el esquema no hay secciones: los cambios se muestran igual, sueltos.
        }
      }

      setPublicado(base);
      setGrupos(secciones);
      setNombreConjunto(nombre);
      setPendiente(data);
    } catch (e) {
      const res = (e as { response?: { data?: { message?: string } } })?.response;
      setError(res?.data?.message ?? "No se pudo cargar la versión pendiente");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /**
   * Las tres acciones comparten forma: piden, avisan y vuelven al listado o recargan. El
   * error se muestra con el mensaje del backend —que trae el código y el motivo— en vez de
   * uno genérico: un `PENDING_CONFLICT` y un `PRODUCT_ADMIN_REQUIRED` se resuelven distinto.
   */
  const ejecutar = async (
    accion: () => Promise<unknown>,
    exito: string,
    volver: boolean,
  ) => {
    setEjecutando(true);
    try {
      await accion();
      message.success(exito);
      if (volver) navigate("/catalog");
      else await cargar();
    } catch (e) {
      const res = (
        e as {
          response?: {
            data?: {
              message?: string;
              code?: string;
              findings?: Array<{ field: string; message: string }>;
            };
          };
        }
      )?.response;
      if (res?.data?.code === "CON_P001_BLOCKING") {
        /**
         * No es un error del operador: es la compuerta diciendo qué falta. Se listan los
         * campos, que es lo único accionable — un toast que sólo dice "falta completar
         * CON-P001" lo obliga a adivinar cuál de los 14.
         */
        const faltan = res.data.findings ?? [];
        Modal.info({
          title: "Falta completar la ficha para pasar a revisión",
          width: 520,
          okText: "Entendido",
          content: (
            <Flex vertical gap={8} style={{ marginTop: 8 }}>
              <Text type="secondary">
                Un borrador se guarda incompleto, pero para mandarlo a revisión hacen
                falta estos campos:
              </Text>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {faltan.map((f) => (
                  <li key={`${f.field}-${f.message}`}>{f.message}</li>
                ))}
              </ul>
              {!faltan.length && <Text>{res.data.message}</Text>}
            </Flex>
          ),
        });
      } else if (res?.data?.code === "PENDING_CONFLICT") {
        // El conflicto no es un error del operador: lo publicado cambió mientras esto
        // esperaba. Se recarga para que el diff muestre de qué se trata.
        message.warning(
          "Lo publicado cambió después de armarse esta versión: revisá el aviso de conflicto",
        );
        await cargar();
      } else {
        message.error(res?.data?.message ?? "No se pudo completar la acción");
      }
    } finally {
      setEjecutando(false);
    }
  };

  const aprobar = () => {
    // **Aprobar es decidir que rijan estos cambios** (Andrés, 2026-09-24), también cuando
    // lo publicado cambió después de armarse la versión. El conflicto no bloquea: se dice
    // qué se va a reemplazar, y el Admin confirma.
    const enConflicto = (pendiente?.diff.entries ?? []).filter((e) => e.conflict);
    Modal.confirm({
      title: enConflicto.length
        ? "¿Querés aprobar y reemplazar lo publicado?"
        : pendiente?.neverPublished
          ? "¿Querés aprobar este producto?"
          : "¿Querés aprobar estos cambios?",
      icon: enConflicto.length ? (
        <WarningOutlined style={{ color: "#faad14" }} />
      ) : (
        <CheckCircleOutlined style={{ color: "#52c41a" }} />
      ),
      width: enConflicto.length ? 560 : undefined,
      content: pendiente?.neverPublished ? (
        "Al aprobarlo, el producto pasa a formar parte del Catálogo y queda disponible para la venta."
      ) : enConflicto.length ? (
        <Space direction="vertical" size={4}>
          <Text>
            Estos campos cambiaron en lo publicado después de armarse la versión. Si
            aprobás, rige lo de la versión:
          </Text>
          {enConflicto.map((e) => (
            <Text key={e.code}>
              • <Text strong>{e.label}</Text>: {e.conflict?.live ?? "—"} →{" "}
              <Text strong>{e.after ?? "—"}</Text>
            </Text>
          ))}
          <Text type="secondary">
            El resto de la versión se aplica igual. Es todo o nada.
          </Text>
        </Space>
      ) : (
        "Los cambios se aplican al producto publicado."
      ),
      okText: pendiente?.neverPublished
        ? "Aprobar"
        : enConflicto.length
          ? "Aprobar y reemplazar"
          : "Aprobar",
      cancelText: "Cancelar",
      onOk: () =>
        ejecutar(
          () =>
            axiosInstance.post(
              `${API_URL_PIM_DRAFTS}/${encodeURIComponent(id)}/approve`,
              enConflicto.length ? { overrideConflicts: true } : undefined,
            ),
          pendiente?.neverPublished ? "Producto publicado" : "Versión aprobada",
          true,
        ),
    });
  };

  const rechazar = () =>
    ejecutar(
      () =>
        axiosInstance.post(
          `${API_URL_PIM_DRAFTS}/${encodeURIComponent(id)}/reject`,
        ),
      "Devuelta a edición",
      true,
    ).then(() => setRechazoAbierto(false));

  const mandarARevision = () =>
    ejecutar(
      () =>
        axiosInstance.post(
          `${API_URL_PIM_DRAFTS}/${encodeURIComponent(id)}/submit`,
        ),
      "Mandada a revisión",
      false,
    );

  /** Los cambios de campos de la ficha, por código de atributo. */
  const cambios = useMemo(
    () =>
      new Map(
        (pendiente?.diff.entries ?? [])
          .filter((e) => AREAS_DE_CAMPOS.includes(e.area))
          .map((e) => [e.code, e]),
      ),
    [pendiente],
  );

  // Al entrar desde la tabla, un spinner y no un esqueleto (xcons-docs#404).
  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (error || !pendiente) {
    return (
      <Card>
        <Alert type="error" showIcon message={error ?? "No existe"} />
        <Button
          style={{ marginTop: 16 }}
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/catalog")}
        >
          Volver al catálogo
        </Button>
      </Card>
    );
  }

  /**
   * **Un borrador se abre en el formulario, tal como se dejó** (Diseño, 2026-09-30):
   * abrirlo es retomar la carga, no mirarla. Lo mismo **un alta en revisión**: quien revisa
   * corrige ahí mismo y aprueba o rechaza desde el formulario. Los cambios en revisión
   * sobre un producto que se vende siguen acá, porque lo que se revisa es qué cambia
   * respecto de lo publicado, y el formulario no lo marca.
   *
   * **Sólo con `canEdit`.** Un `viewer` no puede cargar el esquema del conjunto —es de
   * Editor— y vería el formulario vacío y con un error rojo: se queda en esta vista.
   */
  const seEdita =
    pendiente.state === "draft" ||
    (pendiente.state === "review" && pendiente.neverPublished);
  if (seEdita && pendiente.permissions.canEdit) {
    return <Navigate to={`/catalog/pending/${id}/edit`} replace />;
  }

  const { diff, permissions } = pendiente;
  const alta = pendiente.neverPublished;
  const secciones = seccionesDelForm(grupos);

  const deArea = (area: DiffArea) =>
    diff.entries.filter((e) => e.area === area);
  /** Lo que la versión deja en un campo de la ficha. */
  const queda = (e: DiffEntry) => e.after ?? <Vacio />;
  /** El `cambio` de una celda: sólo si hay algo publicado contra qué compararlo. */
  const marca = (e?: DiffEntry) => (alta ? undefined : e);

  const nombre = cambios.get("name");
  const skuFabricante = skuFabricanteDe(
    pendiente.sku,
    pendiente.manufacturer ?? undefined,
  );
  const identidad = [
    ...(skuFabricante
      ? [
          <Campo key="skuFabricante" label="SKU Fabricante">
            {skuFabricante}
          </Campo>,
        ]
      : []),
    <Campo
      key="sku"
      label={skuFabricante ? "SKU XCONS" : "SKU"}
      cambio={marca(cambios.get("sku"))}
    >
      {pendiente.sku}
    </Campo>,
    <Campo key="name" label="Nombre" cambio={marca(nombre)}>
      {nombre ? queda(nombre) : pendiente.name ?? <Vacio />}
    </Campo>,
  ];

  const valor = (attribute: CatalogAttribute) => {
    const cambio = cambios.get(attribute.code);
    if (cambio) return queda(cambio);
    return publicado ? valorPublicado(publicado, attribute) : <Vacio />;
  };

  /**
   * Los cambios de campos que la ficha no muestra —un atributo fuera de las secciones del
   * form, el conjunto—. Van aparte: un cambio que no se ve es un cambio que se aprueba sin
   * saberlo.
   */
  const enLaFicha = new Set([
    "sku",
    "name",
    ...secciones.flatMap((g) => g.attributes.map((a) => a.code)),
  ]);
  /**
   * **El conjunto es el tipo de producto, y se dice con su nombre** (xcons-docs#404). El
   * diff lo manda como id —"Conjunto de atributos: 20"—, que no le dice nada a quien
   * revisa. En un alta no es un cambio: va como etiqueta junto al nombre y sale de acá.
   * Sobre un publicado sí lo es, y se muestra "Producto simple → Servicio".
   */
  const sueltos = AREAS_DE_CAMPOS.flatMap(deArea)
    .filter((e) => !enLaFicha.has(e.code))
    .filter((e) => !(alta && e.code === "attributeSetId"))
    .map((e) =>
      e.code === "attributeSetId"
        ? {
            ...e,
            label: "Tipo de producto",
            before: publicado?.attributeSet.name
              ? etiquetaConjunto(publicado.attributeSet.name)
              : e.before,
            after: nombreConjunto ? etiquetaConjunto(nombreConjunto) : e.after,
          }
        : e,
    );

  /**
   * Un bloque que no es del EAV: si la versión lo cambia, sus líneas del diff; si no, lo
   * publicado.
   */
  const bloque = (area: DiffArea, deLoPublicado: React.ReactNode) => {
    const entradas = deArea(area);
    if (entradas.length)
      return <CambiosDelBloque entradas={entradas} alta={alta} />;
    return publicado ? deLoPublicado : <Vacio />;
  };

  // La galería de la versión está completa en el payload: se ve lo que queda.
  const media = pendiente.payload?.media ?? publicado?.media ?? [];
  // Y al lado lo que saca de lo publicado, marcado (xcons-docs#499). Los videos no: no
  // tienen miniatura propia que mostrar como imagen.
  const quitadas =
    !alta && pendiente.payload?.media
      ? (publicado?.media ?? []).filter(
          (m) =>
            m.mediaType !== "external-video" &&
            !media.some((x) => x.file === m.file),
        )
      : [];

  /**
   * Las acciones que cambian el estado van en el botón del flujo (xcons-docs#404). Las
   * que el operador no puede usar **se muestran deshabilitadas y explicadas** (R15): un
   * Editor tiene que ver que existe Aprobar y que no le toca a él.
   *
   * En borrador el botón **es** Enviar a revisión, como en el alta: abrir un borrador es
   * retomar el alta donde se dejó. En revisión, Acciones con Aprobar y Rechazar
   * (Diseño, 2026-09-30). Descartar no está en ninguno de los dos.
   */
  // Aprobar y Rechazar sin permiso quedan deshabilitados, sin texto debajo (xcons-docs#479).
  const acciones: AccionDeEstado[] =
    pendiente.state === "draft"
      ? [
          {
            key: "review",
            label: "Enviar a revisión",
            onClick: () => void mandarARevision(),
            disabled: !permissions.canSubmit || ejecutando,
            motivo: permissions.canSubmit
              ? undefined
              : "Lo hace un Editor o Admin de este producto",
          },
        ]
      : [
          {
            key: "approve",
            label: "Aprobar",
            onClick: aprobar,
            disabled: !permissions.canApprove || ejecutando,
          },
          {
            key: "reject",
            label: "Rechazar",
            onClick: () => setRechazoAbierto(true),
            disabled: !permissions.canReject || ejecutando,
          },
        ];

  return (
    <Flex vertical gap={16}>
      {/* Nombre, botones y Estado del producto fijos al hacer scroll (xcons-docs#479). */}
      <CabeceraFija>
        {/* La cabecera de página del Admin, fuera de la tarjeta: ← de 32×32 a 16px del
            título, que es el nombre del producto. */}
        {/* Nombre a la izquierda y el flujo de estado a la derecha, alineados por arriba
            (xcons-docs#404). El flujo reemplaza las etiquetas de estado: en un alta dice en
            qué paso está; en una versión de un producto que se vende, que sigue Activo y que
            lo nuevo todavía no salió. */}
        <Flex justify="space-between" align="flex-start" gap={16}>
          {/* `minWidth: 0` deja que el nombre se corte con "…": sin eso un nombre largo
              empuja el flujo a la línea de abajo. */}
          <Flex align="center" gap={12} style={{ minWidth: 0 }}>
            <Flex align="center" style={{ minWidth: 0 }}>
              <ListButton
                hideText
                icon={<LeftOutlined />}
                onClick={() => navigate("/catalog")}
                style={{ marginRight: 16, flexShrink: 0 }}
              />
              <Title level={4} style={{ margin: 0 }} ellipsis={{ tooltip: true }}>
                {pendiente.name ?? "(sin nombre)"}
              </Title>
            </Flex>
            {/* El tipo de producto, junto al nombre, como en la ficha. */}
            {nombreConjunto && (
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {etiquetaConjunto(nombreConjunto)}
              </Tag>
            )}
          </Flex>
          {/* Permisos en cualquier estado. En un alta todavía no hay dónde guardarlos, y el
              panel lo dice. */}
          <PermisosDelProducto
            productId={pendiente.productId}
            autorUserId={pendiente.authorUserId}
          />
        </Flex>

        {/* El flujo en su propia sección, a todo el ancho, con las fechas en el encabezado. */}
        <ProductStateSteps
          state={alta ? pendiente.state : "active"}
          // Una versión en revisión se marca en En revisión, con Aprobar y Rechazar sobre la
          // línea hacia Activo, que queda gris hasta que se apruebe (Diseño, 2026-10-01).
          paso={!alta && pendiente.state === "review" ? "review" : undefined}
          acciones={acciones}
          // Junto al estado, como en el formulario (xcons-docs#442).
          fechas={{ creado: pendiente.createdAt, editado: pendiente.updatedAt }}
          anchoCompleto
          // La vuelta a lo que está a la venta (xcons-docs#487). Un alta no tiene a dónde.
          etiquetas={
            !alta && (
              <Typography.Link
                onClick={() =>
                  navigate(`/catalog/${encodeURIComponent(pendiente.sku)}`)
                }
              >
                Hay una versión activa de este producto
              </Typography.Link>
            )
          }
        />
      </CabeceraFija>



      {/* Lo que cambia se marca en cada campo de la ficha, sin un resumen arriba
          (Diseño, 2026-10-01), y **sin banner** cuando la versión no cambia nada
          (xcons-docs#479): la ficha sin marcas ya lo dice. */}

      {sueltos.length > 0 && (
        <Card size="small" title="Otros cambios">
          <CambiosDelBloque entradas={sueltos} alta={alta} />
        </Card>
      )}

      <SeccionesDeLaFicha
        secciones={secciones}
        identidad={identidad}
        valor={valor}
        cambios={alta ? undefined : cambios}
        nuevos={
          new Set(
            diff.entries.filter((e) => e.nuevoValor).map((e) => e.code),
          )
        }
        imagenes={{
          contenido: (
            <Flex vertical gap={16}>
              <ImagenesYVideos media={media} quitadas={quitadas} />
              {!alta && deArea("media").length > 0 && (
                <CambiosDelBloque entradas={deArea("media")} alta={alta} />
              )}
            </Flex>
          ),
        }}
        // En el mismo bloque que las secciones; Categorías va aparte (xcons-docs#479).
        extras={[
          {
            key: "adjuntos",
            titulo: "Adjuntos",
            contenido: bloque(
              "adjuntos",
              <Adjuntos attachments={publicado?.attachments ?? []} />,
            ),
          },
          {
            key: "relacionados",
            titulo: "Productos relacionados",
            contenido: bloque("vinculos", <Relacionados links={publicado?.links} />),
          },
          // El stock es del seller y el form no lo carga, pero si la versión lo toca se
          // ve: aprobar un cambio que no está en pantalla es aprobarlo a ciegas.
          ...(deArea("stock").length > 0
            ? [
                {
                  key: "stock",
                  titulo: "Stock",
                  contenido: (
                    <CambiosDelBloque entradas={deArea("stock")} alta={alta} />
                  ),
                },
              ]
            : []),
        ]}
      />

      <Bloque titulo="Categorías">
        {bloque(
          "categorias",
          <CategoriasPorArbol
            categories={publicado?.categories ?? []}
            categoryRoots={publicado?.categoryRoots ?? []}
          />,
        )}
      </Bloque>

      {/* El historial en cualquier etapa (xcons-docs#404). Un alta todavía no tiene
          `entity_id` en Magento, así que se pide por el SKU, que es donde se ancla el
          borrador. */}
      <HistorialDelProducto entityId={pendiente.productId ?? pendiente.sku} />

      <ModalDeRechazo
        open={rechazoAbierto}
        ejecutando={ejecutando}
        onRechazar={rechazar}
        onCancel={() => setRechazoAbierto(false)}
      />
    </Flex>
  );
};
